// React
import { ReactElement, ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// Modules
import { FocusScope } from '@react-aria/focus';
import { useOverlay } from '@react-aria/overlays';
// Design system
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Popin } from '@utmb/design-system/popin/Popin';
// Interfaces
import { IconsEnum } from '@utmb/types/icons';
import { ColorTypesEnum } from '@utmb/types/colors';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
// Styles
import styles from './select-popin.module.scss';

const { FuturaMedium, FuturaBold } = Font;

export interface SelectPopinOption<T extends string> {
    value: T;
    label: ReactNode;
    /** Language of the label, when it differs from the page's (e.g. language names). */
    lang?: string;
}

interface SelectPopinProps<T extends string> {
    /** Class on the wrapper (layout from the caller, e.g. the nav). */
    className?: string;
    /** Icon shown in the trigger, before the current value. */
    icon: IconsEnum;
    /** Short text shown in the trigger (current value). */
    triggerLabel: string;
    /** Adds a chevron after the trigger label (mobile menu). */
    withChevron?: boolean;
    ariaLabel: string;
    title: string;
    options: SelectPopinOption<T>[];
    value: T;
    /** Only called when the picked option differs from `value`. */
    onSelect: (value: T) => void;
}

// Header control: an "icon + value" trigger opening a popin listing every option, the current one checked.
export const SelectPopin = <T extends string>(props: SelectPopinProps<T>): ReactElement => {
    const { className, icon, triggerLabel, withChevron = false, ariaLabel, title, options, value, onSelect } = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const close = (): void => setIsOpen(false);

    const select = (option: T): void => {
        close();
        if (option !== value) onSelect(option);
    };

    return (
        <div className={className}>
            <button
                type="button"
                className={styles.trigger}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                onClick={() => setIsOpen(true)}
            >
                <Icon variant={icon} width={18} height={18} />
                <FuturaMedium mobile="14" as="span">
                    {triggerLabel}
                </FuturaMedium>
                {withChevron && <Icon variant={IconsEnum.CHEVRON_DOWN} />}
            </button>
            {isOpen && (
                <PopinLayer close={close}>
                    <div className={styles.content}>
                        <FuturaBold mobile="18" as="h2" className={styles.title}>
                            {title}
                        </FuturaBold>
                        <ul className={styles.list}>
                            {options.map((option: SelectPopinOption<T>) => {
                                const selected = option.value === value;
                                const Label = selected ? FuturaBold : FuturaMedium;
                                return (
                                    <li key={option.value}>
                                        <button
                                            type="button"
                                            lang={option.lang}
                                            aria-current={selected ? 'true' : undefined}
                                            className={styles.row}
                                            onClick={() => select(option.value)}
                                        >
                                            <Label mobile="16" as="span" className={styles.label}>
                                                {option.label}
                                            </Label>
                                            {selected && (
                                                <Icon
                                                    variant={IconsEnum.CHECKBOX}
                                                    clss={styles.check}
                                                    color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                    width={28}
                                                    height={28}
                                                />
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </PopinLayer>
            )}
        </div>
    );
};

interface PopinLayerProps {
    children: ReactNode;
    close: () => void;
}

// The popin, portaled to <body> so it covers everything (mobile bottom nav included).
const PopinLayer = ({ children, close }: PopinLayerProps): ReactElement => {
    const ref = useRef<HTMLDivElement>(null);
    // Registers the popin as the topmost react-aria overlay: the overlay below it (the mobile menu) then
    // ignores clicks inside the popin instead of closing, and Escape closes the popin only.
    const { overlayProps } = useOverlay({ isOpen: true, onClose: close, isDismissable: false }, ref);

    return createPortal(
        // Child scope of the mobile menu's focus trap (through the React tree), so focus may move into the popin.
        // `autoFocus` here is FocusScope's: it moves focus into the dialog on open, as a modal should.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        <FocusScope contain restoreFocus autoFocus>
            <div ref={ref} {...overlayProps}>
                <Popin closeCallback={close} displayBorderRadius glassBorder>
                    {children}
                </Popin>
            </div>
        </FocusScope>,
        document.body
    );
};
